import { useState } from "react";
import { useToasts } from "../Components/Toasts";
import API from "../Services/API";
import Model from "./Model";
import { toQueryString } from "../Services/helpers";

const useReport = (dateRange, interval) => {
    return Model({
        stateKey: ['admin', 'report', dateRange, interval],
        alwaysRefresh: true,
        fetchData: () => API.get(`admin/reports?dateRange=${dateRange}&interval=${interval}`),
        returnDataKey: 'report',
        defaultLoadingError: 'Sorry, there was an error loading report.',
    })
}

const useNotifications = () => {
    return Model({
        stateKey: ['admin', 'notifications'],
        alwaysRefresh: true,
        fetchData: () => API.get('admin/notifications'),
        returnDataKey: 'notifications',
        defaultLoadingError: 'Sorry, there was an error loading notifications.',
        registerAdditionalMethods: (opts) => {
            return {
                markAllSeen: () => {
                    return opts.wrapUpdateCall(
                        () => API.post('notifications/markAllSeen', { adminJournal: true }),
                        'Notifications cleared',
                        true,
                    );
                },
            }
        }

    })
}

const useBusinesses = (search) => {
    return Model({
        stateKey: ['admin', 'businesses', search],
        alwaysRefresh: true,
        fetchData: () => API.get(`admin/businesses?search=${search}`),
        returnDataKey: 'businesses',
        defaultLoadingError: 'Sorry, there was an error loading businesses.',
    })
}

const useBusiness = (businessId) => {
    return Model({
        stateKey: ['admin', 'business', businessId],
        fetchData: () => API.get(`admin/businesses/${businessId}`),
        returnDataKey: 'business',
        defaultLoadingError: 'Sorry, there was an error loading the business.',
    })
}

const useBalanceAdjustments = (args) => {
    const ret = Model({
        assumeLoading: true,
        returnDataKey: 'data',
        stateKey: null,
        fetchData: () => API.get(composeAdjustmentsUrl(args)),
    });

    return {
        ...ret,
        summary: ret?.data?.summary,
        adjustments: ret?.data?.adjustments,
        expected: ret?.data?.expected,
    }
}

const composeAdjustmentsUrl = (args) => {
    const query = {
        year: args.year ?? '',
        month: args.month ?? '',
        includeTestAccounts: (args.includeTestAccounts ?? false) ? 1 : 0,
        includeNonSoapyRevenue: (args.includeNonSoapyRevenue ?? false) ? 1 : 0,
        includeAnyChangePence: (args.includeAnyChangePence ?? false) ? 1 : 0,
    };
    const download = args.download ?? false;

    const path = `admin/adjustments/monthly${download ? '/download' : ''}`
    const baseUrl = download ? API.domain : '/';

    return `${baseUrl}${path}?${new URLSearchParams(query).toString()}`;
}

const useSendBusinessAdHocNotification = () => {
    const [isSending, setIsSending] = useState(false);
    const {showSuccess, showError} = useToasts();

    const send = (args) => {
        return new Promise((resolve, reject) => {
            setIsSending(true);
            API.post('admin/sendAdHocNotification', args).then((res) => {
                setIsSending(false);
                showSuccess('Notification sent');
                resolve(res);
            }).catch((err) => {
                showError(API.parseErrorResponse(err));
                setIsSending(false);
                reject(err);
            });
        });
    }

    return { isSending, send };
}

const useExperiments = (model, days) => {
    return Model({
        stateKey: ['admin', 'experiments', model, days],
        alwaysRefresh: true,
        fetchData: () => API.get(`/admin/experiments/${model.toLowerCase()}?days=${days}`),
        returnDataKey: 'experiments',
        defaultLoadingError: 'Sorry, there was an error loading experiment data.',
    })
}

const useConversations = (searchData) => {
    searchData.include = 'user,business';
    let searchStr = toQueryString(searchData);

    return Model({
        alwaysRefresh: true,
        stateKey: ['admin', 'conversations', searchStr],
        fetchData: () => API.get(`/assistant/admin/conversations?${searchStr}`),
        returnDataKey: 'conversations',
        defaultLoadingError: 'Sorry, there was an error loading conversations.',
    })
}

const useConversation = (conversationId) => {
    return Model({
        alwaysRefresh: true,
        stateKey: ['admin', 'conversation', conversationId],
        fetchData: () => API.get(`/assistant/admin/conversations/${conversationId}`),
        returnDataKey: 'conversation',
        defaultLoadingError: 'Sorry, there was an error loading the conversation.',
        registerAdditionalMethods: (opts) => {
            return {
                addAdminMessage: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/assistant/admin/conversations/${conversationId}/messages`, data),
                        'Message added',
                        true
                    );
                },
                removeHumanAttention: () => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/assistant/admin/conversations/${conversationId}/remove-human-attention`),
                        'Human attention flag removed',
                        true
                    );
                },
                changeAssistantActiveState: (active) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/assistant/admin/conversations/${conversationId}/change-assistant-active-state`, { active }),
                        'Assistant active state changed',
                        true
                    );
                },
                markAsSeenByAdmin: () => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/assistant/admin/conversations/${conversationId}/seen`),
                        null,
                        true
                    );
                },
                archive: () => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/assistant/admin/conversations/${conversationId}/archive`),
                        'Conversation archived',
                        true
                    );
                },
                star: () => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/assistant/admin/conversations/${conversationId}/star`),
                        'Conversation starred',
                        true
                    );
                },
                unstar: () => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/assistant/admin/conversations/${conversationId}/unstar`),
                        'Conversation unstarred',
                        true
                    );
                },
                protect: () => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/assistant/admin/conversations/${conversationId}/protect`),
                        'Conversation protected',
                        true
                    );
                },
                unprotect: () => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/assistant/admin/conversations/${conversationId}/unprotect`),
                        'Conversation unprotected',
                        true
                    );
                },
            }
        }
    })
}

const levels = {
    EARLY_ACCESS: 'Early Access',
    READ_ONLY: 'Read Only',
    ADMIN: 'Admin',
    SUPER: 'Super',
}

export {
    useReport,
    useNotifications,
    useBusinesses,
    useBusiness,
    useBalanceAdjustments,
    useSendBusinessAdHocNotification,
    useExperiments,
    useConversations,
    useConversation,
    levels,
};
